import { useMemo, useState } from "react";
import type { XRemark } from "../api";
import { ConfirmButton } from "../components/ConfirmButton";
import { Logo } from "../components/Logo";
import { useXRemarkMutations, useXRemarks } from "../queries";
import { Link } from "../router";

type Sort = "recent" | "name";

/**
 * Notes about X accounts, written with the browser extension's pencil next to names on x.com. Its own
 * page because the list grows with everyone you follow: search, sort, edit and delete.
 */
export function XNotes() {
  const remarks = useXRemarks();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<Sort>("recent");
  const all = remarks.data ?? [];

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase().replace(/^@/, "");
    const found = needle
      ? all.filter((r) =>
          [r.remark, r.handle, r.name ?? "", ...r.pastHandles.flatMap((p) => [p.handle, p.name ?? ""])].some((t) => t.toLowerCase().includes(needle)),
        )
      : all;
    if (sort === "recent") return found; // the API lists the most recently changed first
    const label = (r: XRemark) => (r.name || r.handle).toLowerCase();
    return [...found].sort((a, b) => label(a).localeCompare(label(b)));
  }, [all, q, sort]);

  return (
    <main className="settings x-notes-page">
      <header className="settings-header">
        <Link to="/" className="btn btn-small">
          ← Notes
        </Link>
        <Logo />
      </header>
      <h1>Notes on X accounts</h1>
      <p className="muted">
        With the browser extension, click the pencil next to any name on x.com (posts, replies, profiles, Following and Followers) to write who
        they are. Notes follow the account even if it changes its name or @handle. Only you see them.
      </p>

      <div className="files-toolbar">
        <input className="x-notes-search" type="search" placeholder="Search notes, names, handles…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="files-sort">
          <span className="muted small">Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="recent">Recently changed</option>
            <option value="name">Name</option>
          </select>
        </label>
      </div>

      {remarks.isPending ? (
        <p className="muted">Loading…</p>
      ) : remarks.isError ? (
        <p className="error">{(remarks.error as Error).message}</p>
      ) : all.length === 0 ? (
        <p className="muted">No notes yet. Install the ccnote browser extension, then click the pencil next to a name on x.com.</p>
      ) : (
        <>
          <p className="muted small">
            {q.trim() ? `${shown.length} of ${all.length}` : `${all.length} ${all.length === 1 ? "note" : "notes"}`}
          </p>
          <ul className="item-list">
            {shown.map((r) => (
              <XNoteRow key={r.xUserId} remark={r} />
            ))}
          </ul>
        </>
      )}
    </main>
  );
}

function XNoteRow({ remark: r }: { remark: XRemark }) {
  const { save, remove } = useXRemarkMutations();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.remark);
  const profile = `https://x.com/i/user/${r.xUserId}`; // works even after a handle change

  return (
    <li className="x-remark">
      <div className="x-remark-body">
        <div>
          <a href={profile} target="_blank" rel="noreferrer">
            <strong>{r.name || `@${r.handle}`}</strong>
          </a>{" "}
          {r.name && <span className="muted small">@{r.handle}</span>}
          {r.pastHandles.length > 0 && (
            <span className="muted small"> · was {r.pastHandles.map((p) => (p.name && p.name !== r.name ? `${p.name} @${p.handle}` : `@${p.handle}`)).join(", ")}</span>
          )}
        </div>
        {editing ? (
          <form
            className="x-remark-edit"
            onSubmit={(e) => {
              e.preventDefault();
              const value = text.trim();
              if (!value) return remove.mutate(r.xUserId);
              save.mutate({ ...r, remark: value }, { onSuccess: () => setEditing(false) });
            }}
          >
            <textarea rows={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
            <div className="x-remark-actions">
              <button className="btn btn-small" type="button" onClick={() => (setEditing(false), setText(r.remark))}>
                Cancel
              </button>
              <button className="btn btn-small btn-primary" disabled={save.isPending}>
                Save
              </button>
            </div>
          </form>
        ) : (
          <p className="x-remark-text">{r.remark}</p>
        )}
      </div>
      {!editing && (
        <div className="x-remark-actions">
          <button className="btn btn-small" onClick={() => setEditing(true)}>
            Edit
          </button>
          <ConfirmButton label="Delete" confirmLabel="Delete note?" busy={remove.isPending} onConfirm={() => remove.mutate(r.xUserId)} />
        </div>
      )}
    </li>
  );
}
